import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Database,
  BarChart3,
  Filter,
  Search,
  Target,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  ArrowRight,
  MapPin,
  Clock,
  Mail,
  Zap,
  DollarSign,
  Eye,
  Layers,
  Activity,
  HelpCircle,
  ChevronRight,
  Briefcase,
  FileSpreadsheet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20busco%20un%20analista%20de%20datos%20para%20mi%20empresa%20en%20Murcia";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const FAQS = [
  {
    q: "¿Qué diferencia hay entre un analista de datos y un consultor de datos?",
    a: "El analista de datos en Murcia se centra en el trabajo de campo: limpiar la información, montar dashboards, calcular KPIs y entregar informes accionables. El consultor define la estrategia y el plan. En proyectos pequeños y medianos suele asumir las dos figuras la misma persona."
  },
  {
    q: "¿Qué herramientas usa un analista de datos en Murcia?",
    a: "Las habituales: Excel avanzado, SQL, Power BI y Python para automatizaciones. La elección depende del tamaño del proyecto y de las fuentes de datos de la empresa. Lo importante no es la herramienta, sino que los números terminen siendo útiles para tomar decisiones."
  },
  {
    q: "¿Cuándo conviene a una empresa contratar un analista de datos externo?",
    a: "Cuando los datos están dispersos, cuando los cierres mensuales tardan demasiado o cuando dirección sospecha que hay fugas de margen pero no puede demostrarlo con cifras. Externalizar evita el coste fijo de un perfil interno hasta que el volumen lo justifique."
  },
  {
    q: "¿Cómo se mide el resultado del trabajo de un analista de datos?",
    a: "En decisiones tomadas y dinero recuperado, no en informes entregados. Un buen analista debería poder señalar fugas concretas (clientes no rentables, productos con margen negativo, costes ocultos) y dejar el camino claro para corregirlas."
  },
  {
    q: "¿Es el análisis de datos solo para empresas grandes?",
    a: "No. Las PYMEs son las que más impacto suelen ver, porque cada euro mal invertido pesa más en su cuenta de resultados. Si tu empresa factura, vende o compra, ya tiene datos suficientes para que un analista pueda extraer valor de ellos."
  }
];

export default function AnalistaDatosMurcia() {
  useEffect(() => {
    document.title = "Analista de Datos en Murcia para Empresas | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Analista de datos en Murcia especializado en limpiar, analizar y convertir los datos de tu empresa en decisiones que mejoran el margen. Diagnóstico gratuito de 30 minutos."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/analista-datos-murcia");
    }

    // FAQPage JSON-LD
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "analista-faq-jsonld";
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(ldScript);

    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc)
        metaDesc.setAttribute(
          "content",
          "Transforma los datos de tu empresa en decisiones rentables. Servicios de análisis de datos, business intelligence y modelos predictivos en Murcia."
        );
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
      const existing = document.getElementById("analista-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="analista-page">
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="analista-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4" data-testid="analista-nav-logo">
              <img src={LOGO_URL} alt="DATYON - Analista de datos en Murcia" className="h-20 w-auto" style={{ maxHeight: "80px" }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative min-h-[80vh] flex items-center section-padding pt-24 overflow-hidden" data-testid="analista-hero">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="spotlight-2 top-1/3 right-0" />
          <div className="max-w-7xl mx-auto w-full relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
              <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <p className="text-indigo-400 font-medium text-sm md:text-base">Datyon — Murcia, España</p>
              </motion.div>
              <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-testid="analista-h1">
                Analista de datos en Murcia{" "}
                <span className="text-gradient">para empresas</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg mb-4 max-w-2xl">
                Convierto los datos dispersos de tu empresa —ERP, ventas, costes, facturación, hojas de cálculo—
                en información ordenada y útil. Identifico fugas de margen, monto los KPIs que de verdad importan
                y te dejo dashboards claros para que dirección decida con cifras, no con intuición.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-slate-300 text-base md:text-lg mb-8 max-w-2xl font-medium">
                En una sesión de 30 minutos puedo decirte dónde está perdiendo dinero tu empresa.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="analista-hero-cta">
                  <Link to="/contacto">
                    Reserva tu sesión gratuita
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30" data-testid="analista-hero-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                    Hablar por WhatsApp
                  </a>
                </Button>
              </motion.div>
              <motion.p variants={fadeInUp} className="text-slate-500 text-sm mt-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Respuesta en menos de 24 horas · Sin compromiso
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Qué hace un analista de datos */}
        <section className="relative section-padding bg-slate-900/50" data-testid="analista-que-hace">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="analista-h2-que-hace">
                  Qué hace exactamente un{" "}
                  <span className="text-gradient">analista de datos</span> en tu empresa
                </h2>
                <p className="text-slate-400 max-w-3xl text-base md:text-lg leading-relaxed">
                  El trabajo del analista de datos no es escribir informes bonitos: es transformar la información
                  desordenada que ya tienes en respuestas claras a preguntas de negocio. Estas son las cuatro
                  tareas que ocupan el 90 % del tiempo en cualquier proyecto serio.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: <Filter className="w-6 h-6" />,
                    title: "Limpiar y unificar fuentes de datos",
                    desc: "ERP, CRM, Excel, facturación, marketing… cada sistema habla un idioma distinto. El primer paso siempre es ordenarlo: detectar duplicados, corregir errores y construir un único modelo de datos coherente."
                  },
                  {
                    icon: <Search className="w-6 h-6" />,
                    title: "Analizar y encontrar patrones",
                    desc: "Una vez los datos están limpios, llega lo bueno: cruzar información, segmentar clientes, calcular márgenes reales por línea de negocio y descubrir dónde se gana y dónde se pierde dinero."
                  },
                  {
                    icon: <BarChart3 className="w-6 h-6" />,
                    title: "Construir dashboards útiles",
                    desc: "Paneles diseñados para que dirección lea el estado del negocio en 30 segundos. Pocos KPIs, bien elegidos, actualizados solos. Nada de gráficos decorativos que nadie mira pasada la primera semana."
                  },
                  {
                    icon: <FileSpreadsheet className="w-6 h-6" />,
                    title: "Entregar informes accionables",
                    desc: "Cada análisis termina con conclusiones claras y recomendaciones concretas. No 50 páginas de gráficos; 5 decisiones que la empresa puede tomar la semana siguiente con un impacto medible en el margen."
                  }
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className="glass-card rounded-xl p-6 h-full" data-testid={`analista-tarea-${i}`}>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center text-indigo-400 flex-shrink-0 mt-1">
                          {item.icon}
                        </div>
                        <div>
                          <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                          <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Diferencia con otros perfiles */}
        <section className="relative section-padding overflow-hidden" data-testid="analista-diferencias">
          <div className="spotlight-1 top-0 right-0" />
          <div className="max-w-5xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="analista-h2-diferencias">
                  Analista de datos, programador o consultor:{" "}
                  <span className="text-gradient">no es lo mismo</span>
                </h2>
                <p className="text-slate-400 max-w-3xl text-base md:text-lg leading-relaxed">
                  Confundir estos perfiles es uno de los motivos por los que los proyectos de datos fallan en
                  empresas medianas. Cada uno aporta valor en un sitio distinto del proceso.
                </p>
              </motion.div>

              <div className="space-y-4">
                {[
                  { role: "Programador / desarrollador", focus: "Construye software y sistemas. Necesario cuando hay que crear una herramienta nueva, no cuando hay que extraer valor de datos existentes." },
                  { role: "Asesoría informática", focus: "Mantiene servidores, redes y soporte técnico. Imprescindible para que todo funcione, pero no entra a analizar el negocio." },
                  { role: "Consultor de datos", focus: "Define la estrategia: qué medir, qué cambiar, hacia dónde llevar la cultura de datos de la empresa. Trabaja con dirección a alto nivel." },
                  { role: "Analista de datos en Murcia (lo que yo hago)", focus: "Pongo las manos en los datos. Los limpio, los analizo, monto los dashboards y entrego conclusiones aplicables. Es el perfil que más rápido convierte datos en decisiones de margen.", highlight: true }
                ].map((item, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className={`rounded-xl p-5 ${item.highlight ? "bg-indigo-600/10 border border-indigo-500/30" : "bg-slate-900 border border-slate-800"}`} data-testid={`analista-diff-${i}`}>
                      <h3 className={`font-semibold mb-1 ${item.highlight ? "text-white" : "text-slate-200"}`}>{item.role}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.focus}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.p variants={fadeInUp} className="text-slate-400 text-base leading-relaxed mt-10">
                Si lo que buscas es alguien con una visión más estratégica del proyecto, también ofrezco mis
                servicios como{" "}
                <Link to="/consultor-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  consultor de datos en Murcia
                </Link>
                {" "}y como{" "}
                <Link to="/consultor-power-bi-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  consultor Power BI en Murcia
                </Link>
                . En muchos proyectos asumo las dos figuras.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Áreas */}
        <section className="relative section-padding bg-slate-900/50" data-testid="analista-areas">
          <div className="max-w-7xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="analista-h2-areas">
                  Áreas en las que más{" "}
                  <span className="text-gradient">recupero margen</span>
                </h2>
                <p className="text-slate-400 max-w-3xl mx-auto text-base leading-relaxed">
                  Estos son los frentes donde el trabajo de un analista de datos suele dar resultados
                  más rápidos y medibles en empresas de Murcia y alrededores.
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: <DollarSign className="w-6 h-6" />, title: "Rentabilidad por cliente", desc: "Identificar qué clientes son rentables y cuáles cuestan más de lo que aportan." },
                  { icon: <Target className="w-6 h-6" />, title: "Margen por producto", desc: "Calcular el margen real (no el comercial) descontando todos los costes asociados." },
                  { icon: <Activity className="w-6 h-6" />, title: "Análisis de ventas", desc: "Cruzar canales, zonas, comerciales y estacionalidad para entender qué funciona." },
                  { icon: <Database className="w-6 h-6" />, title: "Control de stock", desc: "Detectar stock muerto, exceso de inventario y referencias que descapitalizan caja." },
                  { icon: <TrendingDown className="w-6 h-6" />, title: "Costes ocultos", desc: "Localizar partidas de gasto descontroladas o procesos manuales que cuestan más de lo visible." },
                  { icon: <Briefcase className="w-6 h-6" />, title: "KPIs ejecutivos", desc: "Construir el cuadro de mando que dirección puede leer en 30 segundos cada lunes." }
                ].map((area, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="h-full bg-slate-900 border-slate-800 card-hover" data-testid={`analista-area-${i}`}>
                      <CardContent className="p-6">
                        <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center text-indigo-400 mb-4">
                          {area.icon}
                        </div>
                        <h3 className="font-semibold text-white mb-2">{area.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{area.desc}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <motion.div variants={fadeInUp} className="text-center mt-10">
                <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium inline-flex items-center gap-2 transition-colors" data-testid="analista-link-analisis">
                  Conoce el servicio completo de análisis de datos para empresas <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Cómo trabajo */}
        <section className="relative section-padding" data-testid="analista-proceso">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="analista-h2-proceso">
                  Cómo trabajo, paso a paso
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto text-base leading-relaxed">
                  Un proceso simple, sin reuniones interminables ni informes que nadie abre.
                </p>
              </motion.div>

              <div className="space-y-6">
                {[
                  { step: "01", title: "Sesión inicial de 30 minutos", desc: "Hablamos de tu negocio y de qué decisiones te gustaría poder tomar con datos. En esa misma llamada te digo si puedo ayudarte y por dónde empezaría." },
                  { step: "02", title: "Auditoría rápida de tus datos", desc: "Reviso qué información tienes ya disponible (ERP, CRM, Excel) y en qué estado está. El resultado es un mapa claro de qué se puede analizar y qué hay que limpiar antes." },
                  { step: "03", title: "Limpieza y modelado", desc: "Conecto las fuentes, corrijo errores, elimino duplicados y dejo el modelo de datos listo para que cualquier análisis sobre él sea fiable." },
                  { step: "04", title: "Análisis y dashboards", desc: "Calculo los KPIs que importan, monto los paneles y te enseño a interpretarlos. A partir de aquí, ver el estado del negocio cuesta segundos, no días." },
                  { step: "05", title: "Recomendaciones accionables", desc: "Te entrego un informe con conclusiones concretas: qué cambiar, dónde recortar, qué medir a partir de ahora. No abstracciones, decisiones." }
                ].map((s, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <div className="glass-card rounded-xl p-6 flex items-start gap-6" data-testid={`analista-paso-${i}`}>
                      <div className="text-3xl font-extrabold text-indigo-400/30 flex-shrink-0 leading-none">{s.step}</div>
                      <div>
                        <h3 className="font-semibold text-white mb-2">{s.title}</h3>
                        <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA intermedia con calculadora */}
        <section className="relative section-padding" data-testid="analista-cta-intermedia">
          <div className="max-w-4xl mx-auto">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              <div className="glass-card rounded-2xl p-8 md:p-10 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                  ¿Quieres una idea rápida de cuánto te está costando{" "}
                  <span className="text-gradient">no analizar bien tus datos</span>?
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto mb-6 text-base leading-relaxed">
                  En menos de un minuto, la calculadora ROI te da una estimación honesta de cuánto dinero
                  podría estar perdiendo tu empresa por falta de visibilidad.
                </p>
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-7 py-5 text-base rounded-full" data-testid="analista-link-roi">
                  <Link to="/calculadora-roi-power-bi-murcia">
                    Abrir la calculadora ROI
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative section-padding overflow-hidden" data-testid="analista-faq">
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="analista-h2-faq">
                  Preguntas frecuentes sobre el{" "}
                  <span className="text-gradient">analista de datos en Murcia</span>
                </h2>
                <p className="text-slate-400 text-base leading-relaxed">
                  Resolvemos las dudas más habituales antes de empezar un proyecto.
                </p>
              </motion.div>

              <div className="space-y-4">
                {FAQS.map((faq, i) => (
                  <motion.div key={i} variants={fadeInUp}>
                    <Card className="bg-slate-900 border-slate-800" data-testid={`analista-faq-${i}`}>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-white mb-3 flex items-start gap-3">
                          <HelpCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                          {faq.q}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed pl-8">{faq.a}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* SEO content block */}
        <section className="relative section-padding bg-slate-900/50" data-testid="analista-seo-content">
          <div className="max-w-4xl mx-auto">
            <div className="text-slate-400 text-base leading-relaxed space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Por qué contratar un analista de datos en Murcia y no internalizarlo
              </h2>
              <p>
                Internalizar un perfil de analista de datos junior en plantilla cuesta, entre salario, cargas y
                estructura, fácilmente más de 35 000 € al año. Para la mayoría de PYMEs de Murcia eso es un
                coste fijo que no se justifica hasta que el volumen de datos es muy alto. Un analista externo
                resuelve los puntos críticos —limpiar datos, montar dashboards, calcular KPIs— en semanas y
                a coste cerrado, sin compromiso permanente.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-3">El analista pone las manos en los datos</h3>
              <p>
                La diferencia con un consultor es práctica. El consultor traza el plan, pero el analista es
                quien escribe las consultas SQL, limpia el Excel, conecta el ERP a Power BI y deja el panel
                funcionando. Esa parte operativa es justo donde la mayoría de empresas se atascan, y es la
                que más tiempo ahorra externalizar.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-3">Ámbito local: Murcia y resto de España</h3>
              <p>
                Trabajo principalmente con empresas de Murcia capital, Cartagena, Lorca, Molina de Segura y
                resto de la Región. Las reuniones clave las hago presenciales cuando estás cerca y el resto
                en remoto. Para clientes fuera de la Región, todo el proyecto se ejecuta por videollamada
                con la misma metodología.
              </p>

              <h3 className="text-xl font-bold text-white mt-8 mb-3">Y si quieres profundizar en métricas</h3>
              <p>
                En el blog explico qué KPIs son los más relevantes para saber si una empresa está siendo
                rentable. Si te interesa el tema, este artículo te puede dar contexto antes de cualquier
                conversación:{" "}
                <Link to="/blog/que-kpis-medir-empresa-rentable" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  qué KPIs debe medir una empresa para ser rentable
                </Link>
                . También te puede interesar el servicio de{" "}
                <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                  business intelligence en Murcia
                </Link>
                {" "}cuando lo que necesitas no es solo análisis puntual sino una capa completa de BI.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative section-padding bg-gradient-to-b from-slate-900/80 to-[#020617]" data-testid="analista-cta-final">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold mb-4" data-testid="analista-h2-cta-final">
                Pongamos a trabajar{" "}
                <span className="text-gradient">tus datos</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-3 max-w-xl mx-auto text-base leading-relaxed">
                Si quieres dejar de gestionar tu empresa con la sensación de ir a ciegas, hablemos. En 30 minutos
                te digo dónde está perdiendo dinero tu empresa y cómo el análisis de datos puede recuperarlo.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-indigo-400 font-medium mb-8 text-base">
                Diagnóstico gratuito · Sin compromiso · Respuesta en menos de 24 horas
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full" data-testid="analista-cta-final-contact">
                  <Link to="/contacto">
                    Reservar mi sesión de 30 minutos
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base rounded-full transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-600/30" data-testid="analista-cta-final-whatsapp">
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                    Hablar por WhatsApp
                  </a>
                </Button>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-400 text-sm">
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <a href="mailto:info@datyon.com" className="hover:text-white transition-colors">info@datyon.com</a>
                </span>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800" data-testid="analista-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        data-testid="analista-whatsapp-float" aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          ¿Hablamos?
        </span>
      </a>

      <CookieBanner />
    </div>
  );
}
